import request from '@/utils/request'

// ==================== 文件仓库接口 ====================
// 接口前缀：/admin/upload
// 响应拦截器已统一处理 code/msg，返回后端完整 res（含 code/msg/data），业务数据取 res.data
// 注意：上传类接口（upload / part）timeout 设为 0，避免大文件上传被 60s 超时打断

// ============ 文件管理 ============

/**
 * 文件列表（分页）
 * @param {Object} params
 * @param {number} [params.page] - 页码，默认 1
 * @param {number} [params.pageSize] - 每页条数，默认 20，最大 100
 * @param {number} [params.file_type] - 文件类型：10图片 20视频 30文件，0=全部
 * @param {number} [params.category_id] - 分类ID，0=全部，-1=未分类
 * @param {string} [params.keyword] - 文件名搜索关键词
 * @returns {Promise} res - res.data 为 { list, total, page, pageSize }
 */
export function getFileList(params) {
    return request({
        url: '/admin/upload/file/list',
        method: 'get',
        params
    })
}

/**
 * 文件选择器数据（网格展示用的精简列表）
 * @param {Object} params
 * @param {number} [params.page]
 * @param {number} [params.pageSize] - 默认 24
 * @param {number} [params.file_type]
 * @param {number} [params.category_id]
 * @param {string} [params.keyword]
 * @returns {Promise} res
 */
export function getFilePicker(params) {
    return request({
        url: '/admin/upload/file/picker',
        method: 'get',
        params
    })
}

/**
 * 仓库统计（文件数量 + 总占用）
 * @returns {Promise} res - res.data 为 { total, image, video, file, size }
 */
export function getFileStats() {
    return request({
        url: '/admin/upload/file/stats',
        method: 'get'
    })
}

/**
 * 普通文件上传（小文件，单文件）
 * @param {FormData} data - 含 file / category_id?
 * @param {(e: ProgressEvent) => void} [onUploadProgress] - 上传进度回调
 * @returns {Promise} res - res.data 为 { file_id, file_url }（秒传时多一个 instant）
 */
export function uploadFileSimple(data, onUploadProgress) {
    return request({
        url: '/admin/upload/file/upload',
        method: 'post',
        data,
        timeout: 0,
        onUploadProgress
    })
}

/**
 * 批量删除文件
 * @param {number[]} file_ids - 文件ID数组
 * @returns {Promise} res - res.data 为 { deleted }
 */
export function batchDeleteFile(file_ids) {
    return request({
        url: '/admin/upload/file/batchDelete',
        method: 'post',
        data: { file_ids }
    })
}

/**
 * 批量移动文件到分类
 * @param {number[]} file_ids - 文件ID数组
 * @param {number} categoryId - 目标分类ID（0=未分类）
 * @returns {Promise} res
 */
export function batchMoveFile(file_ids, categoryId) {
    return request({
        url: '/admin/upload/file/batchMove',
        method: 'post',
        data: { file_ids, category_id: categoryId }
    })
}

// ============ 分片上传 ============

/**
 * 初始化分片上传
 * 后端根据 file_hash 和存储驱动返回四种状态：
 *   - fallback_simple：当前存储不支持分片，降级普通上传（附带 max_size）
 *   - instant：秒传，文件已存在，直接返回 data.file
 *   - resume：断点续传，跳过 uploaded_parts
 *   - new：全新任务，从第 1 片开始上传
 * @param {Object} data
 * @param {string} data.file_name
 * @param {number} data.file_size
 * @param {string} data.file_hash - 前端计算的 SHA256
 * @param {string} [data.file_ext]
 * @param {number} [data.category_id]
 * @returns {Promise} res
 */
export function multipartInit(data) {
    return request({
        url: '/admin/upload/multipart/init',
        method: 'post',
        data
    })
}

/**
 * 上传单个分片
 * @param {FormData} data - 含 upload_id / part_number / part
 * @param {(e: ProgressEvent) => void} [onUploadProgress]
 * @returns {Promise} res - res.data 为 { part_number, etag }
 */
export function multipartPart(data, onUploadProgress) {
    return request({
        url: '/admin/upload/multipart/part',
        method: 'post',
        data,
        timeout: 0,
        onUploadProgress
    })
}

/**
 * 完成分片上传（合并文件）
 * @param {string} upload_id - 上传任务ID
 * @returns {Promise} res - res.data 为 { file_id, file_url }
 */
export function multipartComplete(upload_id) {
    return request({
        url: '/admin/upload/multipart/complete',
        method: 'post',
        data: { upload_id }
    })
}

/**
 * 取消分片上传（清理已上传分片）
 * @param {string} upload_id - 上传任务ID
 * @returns {Promise} res
 */
export function multipartAbort(upload_id) {
    return request({
        url: '/admin/upload/multipart/abort',
        method: 'post',
        data: { upload_id }
    })
}

/**
 * 查询分片上传状态（断点续传恢复用）
 * @param {string} upload_id - 上传任务ID
 * @returns {Promise} res
 */
export function getMultipartStatus(upload_id) {
    return request({
        url: '/admin/upload/multipart/status',
        method: 'get',
        params: { upload_id }
    })
}

// ============ 文件分类（树形 CRUD）============

/**
 * 分类列表（树形结构）
 * @param {Object} [params]
 * @returns {Promise} res - res.data 为树形数组
 *   每项 { id, parent_id, name, sort, create_time, update_time, children? }
 */
export function getCategoryTree(params) {
    return request({
        url: '/admin/upload/category/list',
        method: 'get',
        params
    })
}

/**
 * 分类详情（编辑回显）
 * GET /admin/upload/category/update?id=X
 * @param {number} id - 分类ID
 * @returns {Promise} res
 */
export function getCategoryDetail(id) {
    return request({
        url: '/admin/upload/category/update',
        method: 'get',
        params: { id }
    })
}

/**
 * 创建分类
 * @param {Object} data
 * @param {number} [data.parent_id=0] - 父级ID（0=顶级）
 * @param {string} data.name - 分类名称
 * @param {number} [data.sort=0] - 排序值（越小越靠前）
 * @returns {Promise} res - res.data 为新建分类
 */
export function createCategory(data) {
    return request({
        url: '/admin/upload/category/create',
        method: 'post',
        data
    })
}

/**
 * 更新分类（仅传需要修改的字段，修改 parent_id 时后端会检查循环引用）
 * POST 时 id 放 body
 * @param {Object} data
 * @param {number} data.id - 分类ID
 * @param {string} [data.name]
 * @param {number} [data.sort]
 * @param {number} [data.parent_id]
 * @returns {Promise} res
 */
export function updateCategory(data) {
    return request({
        url: '/admin/upload/category/update',
        method: 'post',
        data
    })
}

/**
 * 删除分类
 * 限制：有关联文件或子分类时无法删除
 * @param {number} id - 分类ID
 * @returns {Promise} res
 */
export function deleteCategory(id) {
    return request({
        url: '/admin/upload/category/delete',
        method: 'post',
        data: { id }
    })
}
